/**
 * Format an `AuthClientError` (or any thrown value) as a user-facing string.
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/i18n-strategy.md § 6`.
 *
 * Resolution order:
 *
 * 1. `AuthClientError.code` → `errors:<CODE>` lookup in i18n. Interpolates
 *    `{{requestId}}` for codes that surface it (e.g., INTERNAL_SERVER_ERROR).
 * 2. Falls back to the server's raw `message` if no mapping exists. This
 *    keeps the UI from going blank when a new error code lands on the API
 *    before the dashboard has translations for it.
 * 3. For non-`AuthClientError` throws (e.g., network failures) returns a
 *    generic "Something went wrong" string.
 */

import { AuthClientError } from "@cometchat-ai/auth/client";

import { AgentsApiError } from "../agents/api/client";
import { i18n } from "../i18n";

export function formatApiError(err: unknown): string {
  if (err instanceof AuthClientError) {
    return i18n.t(`errors:${err.code}`, {
      requestId: err.requestId,
      defaultValue: err.message,
    });
  }
  // The agents/skills REST client throws AgentsApiError (not AuthClientError).
  // Without this branch every agents-API failure fell through to NETWORK_ERROR
  // ("Can't reach the server"), masking the real reason. Translate by code when
  // we have a mapping, else surface the server's own message.
  if (err instanceof AgentsApiError) {
    return i18n.t(`errors:${err.code}`, {
      requestId: err.requestId,
      defaultValue: err.message,
    });
  }
  if (err instanceof Error && err.message) {
    return i18n.t("errors:NETWORK_ERROR", { defaultValue: err.message });
  }
  return i18n.t("common:errors.genericDescription");
}
